// Client-side codec check for release-note videos. We refuse only what we can
// prove browsers cannot play (HEVC, AV1, old MPEG-4 Part 2 / H.263); anything
// the detector cannot identify is allowed. Only headers are read, never the
// media payload.

const MB = 1024 * 1024;
const MAX_MOOV = 16 * MB;
const WEBM_SCAN = 4 * MB;

const UNKNOWN = { container: 'unknown', codec: 'unknown', tag: '' };

const MP4_CODECS = {
  avc1: 'h264',
  avc3: 'h264',
  hvc1: 'hevc',
  hev1: 'hevc',
  av01: 'av1',
  vp08: 'vp8',
  vp09: 'vp9',
  mp4v: 'mpeg4',
  s263: 'mpeg4',
};

const WEBM_CODECS = [
  ['V_VP8', 'vp8'],
  ['V_VP9', 'vp9'],
  ['V_AV1', 'av1'],
  ['V_MPEG4/ISO/AVC', 'h264'],
  ['V_MPEGH/ISO/HEVC', 'hevc'],
  ['V_MPEG4/ISO/ASP', 'mpeg4'],
  ['V_MPEG4/ISO/SP', 'mpeg4'],
];

const fourcc = (bytes, at) => String.fromCharCode(bytes[at], bytes[at + 1], bytes[at + 2], bytes[at + 3]);
const u32 = (bytes, at) => ((bytes[at] << 24) | (bytes[at + 1] << 16) | (bytes[at + 2] << 8) | bytes[at + 3]) >>> 0;

// Child boxes of bytes[start, end): [{ type, body, end }] (body = first content byte).
const childBoxes = (bytes, start, end) => {
  const out = [];
  let at = start;
  while (at + 8 <= end) {
    let size = u32(bytes, at);
    let head = 8;
    if (size === 1) {
      if (at + 16 > end) break;
      size = u32(bytes, at + 8) * 2 ** 32 + u32(bytes, at + 12);
      head = 16;
    } else if (size === 0) {
      size = end - at;
    }
    if (size < head || at + size > end) break;
    out.push({ type: fourcc(bytes, at + 4), body: at + head, end: at + size });
    at += size;
  }
  return out;
};

const findChild = (bytes, parent, type) => childBoxes(bytes, parent.body, parent.end).find((b) => b.type === type);

// The handler type and first sample-entry fourcc of one trak.
const describeTrak = (bytes, trak) => {
  const mdia = findChild(bytes, trak, 'mdia');
  if (!mdia) return { handler: null, tag: null };
  const hdlr = findChild(bytes, mdia, 'hdlr');
  const handler = hdlr && hdlr.body + 12 <= hdlr.end ? fourcc(bytes, hdlr.body + 8) : null;
  const minf = findChild(bytes, mdia, 'minf');
  const stbl = minf && findChild(bytes, minf, 'stbl');
  const stsd = stbl && findChild(bytes, stbl, 'stsd');
  // stsd: version/flags (4) + entry_count (4), then the first entry's box header.
  const tag = stsd && stsd.body + 16 <= stsd.end ? fourcc(bytes, stsd.body + 12) : null;
  return { handler, tag };
};

const parseMoov = (bytes) => {
  const traks = childBoxes(bytes, 0, bytes.length).filter((b) => b.type === 'trak');
  let fallback = null;
  for (const trak of traks) {
    const { handler, tag } = describeTrak(bytes, trak);
    if (!tag) continue;
    if (handler === 'vide') return tag;
    if (handler === null && !fallback && MP4_CODECS[tag]) fallback = tag;
  }
  return fallback;
};

const detectMp4 = async (read, size) => {
  let offset = 0;
  while (offset + 8 <= size) {
    const head = await read(offset, 8);
    if (head.length < 8) return UNKNOWN;
    const type = fourcc(head, 4);
    if (offset === 0 && !['ftyp', 'moov', 'mdat', 'free'].includes(type)) return UNKNOWN;
    let boxSize = u32(head, 0);
    let headLen = 8;
    if (boxSize === 1) {
      const big = await read(offset + 8, 8);
      if (big.length < 8) return UNKNOWN;
      boxSize = u32(big, 0) * 2 ** 32 + u32(big, 4);
      headLen = 16;
    } else if (boxSize === 0) {
      boxSize = size - offset;
    }
    if (boxSize < headLen) return UNKNOWN;
    if (type === 'moov') {
      if (boxSize > MAX_MOOV || offset + boxSize > size) return UNKNOWN;
      const body = await read(offset + headLen, boxSize - headLen);
      const tag = parseMoov(body);
      if (!tag) return UNKNOWN;
      return { container: 'mp4', codec: MP4_CODECS[tag] || 'unknown', tag };
    }
    offset += boxSize;
  }
  return UNKNOWN;
};

const detectWebm = async (read, size) => {
  const bytes = await read(0, Math.min(size, WEBM_SCAN));
  let best = null;
  for (let i = 0; i + 4 < bytes.length; i += 1) {
    if (bytes[i] !== 0x56 || bytes[i + 1] !== 0x5f) continue; // "V_"
    for (const [id, codec] of WEBM_CODECS) {
      let match = i + id.length <= bytes.length;
      for (let j = 0; match && j < id.length; j += 1) match = bytes[i + j] === id.charCodeAt(j);
      if (match && (!best || id.length > best[0].length)) best = [id, codec];
    }
    if (best) break;
  }
  return best ? { container: 'webm', codec: best[1], tag: best[0] } : UNKNOWN;
};

export async function detectVideoCodec(read, size) {
  try {
    const magic = await read(0, 4);
    if (magic.length === 4 && magic[0] === 0x1a && magic[1] === 0x45 && magic[2] === 0xdf && magic[3] === 0xa3) {
      return await detectWebm(read, size);
    }
    return await detectMp4(read, size);
  } catch (e) {
    return UNKNOWN;
  }
}

const MESSAGES = {
  hevc: "This video is HEVC (H.265), which most browsers can't play. Export it as H.264 MP4 and try again.",
  av1: "This video is AV1, which Safari can't play on most devices yet. Export it as H.264 MP4 or VP9 WebM and try again.",
};

export function videoCodecVerdict({ codec, tag }) {
  if (codec === 'hevc') return { ok: false, reason: 'hevc', message: MESSAGES.hevc };
  if (codec === 'av1') return { ok: false, reason: 'av1', message: MESSAGES.av1 };
  if (codec === 'mpeg4') {
    return {
      ok: false,
      reason: 'unsupported',
      message: `This video's codec (${tag}) doesn't play in browsers. Export it as H.264 MP4 or VP9 WebM and try again.`,
    };
  }
  return { ok: true };
}

// A `read(offset, length)` over a File/Blob. FileReader (not Blob.arrayBuffer)
// so it also runs under jsdom.
export function readFileBytes(file) {
  return (offset, length) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(new Uint8Array(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file.slice(offset, offset + length));
    });
}
